<template>
    <transition name="anim-drawer">
        <vrm-drawer
            :is-open="isOpen"
            :title="$t('dashboard.dci.controls')"
            close-icon="sg-vrm-icon_055_cross-16px"
            @close="handleClose"
        >
            <div class="vrm-device-controls">
                <div v-if="!hasDeviceControls || isMqttLoading" class="dci__loading-screen">
                    <vrm-loading class="device-controls__block is-loading">
                        {{ $t('common.connecting', { i18nFallback: 'Connecting...' }) }}
                    </vrm-loading>
                </div>
                <template v-if="hasDeviceControls && !isMqttLoading">
                    <template v-if="hasGeneratorPaths">
                        <generator
                            v-for="generator in generatorInstances"
                            :key="`generator-${generator}`"
                            :identifier="identifier"
                            :instance="generator"
                            :show-instance="generatorInstances.length > 1"
                            :installation-id="installationId"
                            :timer-limit="timerLimit"
                        />
                    </template>
                    <charging-station
                        v-for="instance in chargingStationInstances"
                        :key="`charging-station-${instance}`"
                        :identifier="identifier"
                        :instance="instance"
                        :installation-id="installationId"
                        :timer-limit="timerLimit"
                    />
                    <template v-if="enabledInverters">
                        <!-- Do not show individual RS instances when AC System is present -->
                        <template v-if="acSystemInstances && acSystemInstances.length === 0">
                            <inverter
                                v-for="instance in inverterInstances"
                                :key="`inverter-${instance}`"
                                :identifier="identifier"
                                :instance="instance"
                                :installation-id="installationId"
                                :timer-limit="timerLimit"
                            />
                            <inverter-rs
                                v-for="instance in inverterRsInstances"
                                :key="`inverter-rs-${instance}`"
                                :identifier="identifier"
                                :instance="instance"
                                :installation-id="installationId"
                                :timer-limit="timerLimit"
                            />
                            <multi-rs
                                v-for="instance in multiRsInstances"
                                :key="`multi-rs-${instance}`"
                                :identifier="identifier"
                                :instance="instance"
                                :installation-id="installationId"
                                :timer-limit="timerLimit"
                            />
                        </template>
                        <ac-system
                            v-for="instance in acSystemInstances"
                            :key="`ac-system-${instance}`"
                            :identifier="identifier"
                            :instance="instance"
                            :installation-id="installationId"
                            :timer-limit="timerLimit"
                        />
                    </template>
                    <template v-if="supportsEss">
                        <ess
                            v-for="instance in essInstances"
                            :key="`ess-${instance}`"
                            :identifier="identifier"
                            :installation-id="installationId"
                            :instance="instance"
                            :timer-limit="timerLimit"
                            :has-dynamic-ess-capability="hasDynamicEssCapability"
                            :dynamic-ess-config-on="dynamicEssOn"
                            :dess-soc-different="dessSocDifferent"
                        />
                        <ac-system-ess
                            v-for="instance in acSystemEssInstances"
                            :key="`ac-system-ess-${instance}`"
                            :identifier="identifier"
                            :installation-id="installationId"
                            :instance="instance"
                            :timer-limit="timerLimit"
                            :has-dynamic-ess-capability="hasDynamicEssCapability"
                            :dynamic-ess-config-on="dynamicEssOn"
                        />
                    </template>
                    <switches
                        v-for="instance in systemInstances"
                        :key="`switches-${instance}`"
                        :identifier="identifier"
                        :installation-id="installationId"
                        :instance="instance"
                        :timer-limit="timerLimit"
                    />
                    <activate-inverters-card v-if="!enabledInverters && inverterInstances.length" />
                </template>
            </div>
        </vrm-drawer>
    </transition>
</template>
<script>
import { uniq } from 'lodash';

import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { AngularDirective, ngInject } from '@/vue/decorators/angular-interop';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';

import Inverter from './controls/Inverter';
import InverterRs from './controls/InverterRs';
import Generator from './controls/Generator';
import Ess from './controls/Ess';
import AcSystemEss from './controls/AcSystemEss';
import Switches from './controls/Switches';
import ChargingStation from './controls/ChargingStation.vue';
import VrmLoading from '@/vue/components/shared/VrmLoading.vue';
import ActivateInvertersCard from './controls/components/ActivateInvertersCard';
import MultiRs from './controls/MultiRs';
import AcSystem from './controls/AcSystem';

import VrmIconButton from '../../molecules/VrmIconButton';
import VrmDrawer from '../VrmDrawer';
import {
    TIMER_LIMIT,
    DEVICE_INSTANCES_PROPS,
    getRelaysSubscriptions,
    subscribeToInfo,
} from './controls/utils';
import { isEqual } from 'lodash';

@Component({
    components: {
        Inverter,
        InverterRs,
        Generator,
        Ess,
        AcSystemEss,
        VrmIconButton,
        Switches,
        VrmDrawer,
        ChargingStation,
        VrmLoading,
        ActivateInvertersCard,
        MultiRs,
        AcSystem,
    },
})
@AngularLocaleBundle('dashboard')
@AngularLocaleBundle('common')
@AngularDirective('vrm-device-controls')
export default class VrmDeviceControls extends Vue {
    @ngInject()
    MQTTRealtimeService;
    @ngInject()
    $location;
    @ngInject()
    localStorageService;
    @ngInject()
    ApiService;

    @Prop()
    identifier;
    @Prop()
    installationId;

    @Prop({ required: true })
    isOpen;
    @Prop({ required: true })
    hasGeneratorPaths;
    @Prop({ required: true })
    hasDynamicEssCapability;
    @Prop({ required: true })
    hasDynamicEssPermission;
    @Prop({ required: true })
    supportsEss;
    @Prop({ required: true })
    enabledInverters;
    @Prop({ required: true, type: String })
    realtimeIndicator;

    dynamicEssOn = false;
    dessSocDifferent = false;

    // controlled by mqtt
    inverterInstances = [];
    inverterRsInstances = [];
    essInstances = [];
    acSystemEssInstances = [];
    systemInstances = [];
    chargingStationInstances = [];
    generatorInstances = [];
    multiRsInstances = [];
    acSystemInstances = [];
    relayFunction = {
        0: null,
        1: null,
    };
    relays = {
        0: null,
        1: null,
    };

    timerLimit = TIMER_LIMIT;

    mounted() {
        if (this.localStorageService.get('debugDeviceControls')) {
            this.timerLimit = 1;
        }
        this.gatherInstances();
    }

    beforeDestroy() {
        this.unsubscribeFromInfo();
        document.body.style.overflow = '';
        this.$emit('close');
    }

    gatherInstances() {
        DEVICE_INSTANCES_PROPS.forEach(subscriptionOptions => {
            this.MQTTRealtimeService.subscribeToPath({
                ...subscriptionOptions,
                dbusPath: '/DeviceInstance',
                instance: '+',
                identifier: this.identifier,
                callback: message => {
                    if (message.retained) {
                        this.MQTTRealtimeService.publishToPath(
                            {
                                ...subscriptionOptions,
                                dbusPath: '/DeviceInstance',
                                instance: message.payload.value,
                                identifier: this.identifier,
                                prefix: 'R/',
                            },
                            '',
                        );
                        return;
                    }
                    this[subscriptionOptions.varName] = uniq([
                        ...this[subscriptionOptions.varName],
                        message.payload.value,
                    ]);
                },
            });
        });
        const relayRelatedSubscriptions = getRelaysSubscriptions()
            .filter(
                subscription =>
                    subscription.varName === 'relayFunction' || subscription.varName === 'relays',
            )
            .map(subscription => ({
                ...subscription,
                identifier: this.identifier,
                instance: 0,
                callbackIdentifier: 'vrmDeviceControlsRelayFunction' + subscription.varIndx,
            }));
        subscribeToInfo.call(this, {
            subscriptions: relayRelatedSubscriptions,
        });
    }

    unsubscribeFromInfo() {
        DEVICE_INSTANCES_PROPS.forEach(subscriptionOptions => {
            this.MQTTRealtimeService.unsubScribeFromPath({
                ...subscriptionOptions,
                dbusPath: '/DeviceInstance',
                instance: '+',
                identifier: this.identifier,
            });
        });
    }

    get isMqttLoading() {
        return this.realtimeIndicator === this.MQTTRealtimeService.REALTIME_INDICATOR.LOADING;
    }

    get hasDeviceControls() {
        const manualRelays = Object.fromEntries(
            Object.entries(this.relayFunction).filter(([, value]) => value == 2),
        );
        const validRelays = Object.entries(this.relays).filter(([key]) => key in manualRelays);
        return (
            (!!this.essInstances.length && this.supportsEss) ||
            (!!this.acSystemEssInstances.length && this.supportsEss) ||
            !!this.chargingStationInstances.length ||
            (!!this.generatorInstances.length && this.hasGeneratorPaths) ||
            (!!validRelays.length && !!this.systemInstances.length) ||
            !!this.inverterInstances.length
        );
    }

    get hasInverterInstances() {
        return (
            this.inverterInstances.length ||
            this.inverterRsInstances.length ||
            this.multiRsInstances.length ||
            this.acSystemInstances.length
        );
    }

    @Watch('hasDeviceControls')
    onDeviceControlsChange() {
        this.$emit('set-has-device-controls', this.hasDeviceControls);
    }

    get essComputedValues() {
        return {
            essInstances: this.essInstances,
            acSystemEssInstances: this.acSystemEssInstances,
            supportsEss: this.supportsEss,
        };
    }

    @Watch('essComputedValues')
    onEssPresent(newValues, oldValues) {
        if (
            !newValues.supportsEss ||
            !this.hasDynamicEssPermission ||
            (newValues.essInstances.length === 0 && newValues.acSystemEssInstances.length === 0) ||
            (newValues.essInstances.length != 0 &&
                isEqual(newValues.essInstances, oldValues.essInstances)) ||
            (newValues.acSystemEssInstances.length != 0 &&
                isEqual(newValues.acSystemEssInstances, oldValues.acSystemEssInstances))
        ) {
            return;
        }

        this.ApiService.call(
            '/installations/' + this.installationId + '/dynamic-ess-settings',
            {},
            'GET',
            false,
            'json',
            true,
        ).then(response => {
            if (!response.data.success) {
                return;
            }
            const hasDynamicEssConfiguration =
                response.data.data &&
                Object.hasOwn(response.data.data, 'isOn') &&
                response.data.data.buyPriceType !== null;
            if (hasDynamicEssConfiguration) {
                this.dynamicEssOn = response.data.data.isOn;
                this.dessSocDifferent = response.data.data.isDessSocDifferent;
            }
        });
    }

    handleClose() {
        this.$emit('close');
    }
}
</script>
